/**
 * Staff-facing landing page — lists my assignments.
 * Click an assignment → opens the split-screen TrainingSession.
 */
import React, { useEffect, useRef, useState } from 'react';
import {
  TrainingAssignment, TrainingCertificate, CertificateListResponse,
  listMyAssignments, listMyCertificates, openCertificate,
} from '../../api/training';
import { TrainingSession } from './TrainingSession';

// Animated integer count-up — used by the hero progress ring so the label
// smoothly counts from 0 → target % on mount rather than snapping.
function useCountUp(target: number, durationMs = 1200): number {
  const [value, setValue] = useState(0);
  const ref = useRef(0);
  useEffect(() => {
    const start = Date.now();
    const from = ref.current;
    const delta = target - from;
    let rafId: number;
    const tick = () => {
      const t = Math.min(1, (Date.now() - start) / durationMs);
      // ease-out-cubic for a natural settle at the end
      const eased = 1 - Math.pow(1 - t, 3);
      const next = Math.round(from + delta * eased);
      setValue(next);
      ref.current = next;
      if (t < 1) rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [target, durationMs]);
  return value;
}

// Compact purple readiness ring for the segmented training banner.
function BannerRing({ pct }: { pct: number }) {
  const size = 54, stroke = 6, r = size / 2 - stroke / 2, circ = 2 * Math.PI * r;
  const animated = useCountUp(Math.max(0, Math.min(100, pct)));
  const offset = circ - (animated / 100) * circ;
  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--border-md)" strokeWidth={stroke} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--primary)" strokeWidth={stroke}
          strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset 0.3s ease' }} />
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center' }}>
        <span style={{ fontSize: 14, fontWeight: 800, color: 'var(--fg)', fontFamily: 'var(--font-heading)', letterSpacing: '-0.02em' }}>{animated}%</span>
      </div>
    </div>
  );
}

// Segmented readiness banner — the training header. Same content the old
// gradient hero showed (readiness ring + Completed / In progress / Total time),
// re-laid-out as a four-slot banner. No metrics added or removed.
function TrainingReadinessBanner({ assignments }: { assignments: TrainingAssignment[] }) {
  const totalAssigned = assignments.length;
  const completed = assignments.filter(a => a.status === 'COMPLETED').length;
  const inProgress = assignments.filter(a => a.status === 'IN_PROGRESS').length;
  const pct = totalAssigned === 0 ? 0 : Math.round(
    assignments.reduce((sum, a) => sum + (a.status === 'COMPLETED' ? 100 : (a.videoProgressPercent || 0)), 0) / totalAssigned,
  );
  // No per-module duration in the API yet — rough ~25 min/module estimate.
  const estHours = totalAssigned === 0 ? 0 : Math.max(1, Math.round((totalAssigned * 25) / 60));

  const eyebrow: React.CSSProperties = {
    fontSize: 10.5, fontWeight: 700, letterSpacing: '0.1em',
    textTransform: 'uppercase', color: 'var(--subtle)', marginBottom: 8,
  };
  const seg: React.CSSProperties = {
    flex: 1, minWidth: 150, padding: '18px 24px',
    display: 'flex', flexDirection: 'column', justifyContent: 'center',
  };
  const segBorder: React.CSSProperties = { ...seg, borderLeft: '1px solid var(--border)' };
  const big: React.CSSProperties = {
    fontSize: 26, fontWeight: 800, color: 'var(--fg)', letterSpacing: '-0.02em', lineHeight: 1,
  };

  return (
    <div style={{
      background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 16,
      display: 'flex', flexWrap: 'wrap', alignItems: 'stretch',
      marginBottom: 24, overflow: 'hidden',
    }}>
      {/* Overall readiness — ring + READY (as the old hero ring showed) */}
      <div style={{ ...seg, flex: '1.4 1 240px', flexDirection: 'row', alignItems: 'center', gap: 16 }}>
        <BannerRing pct={pct} />
        <div style={{ minWidth: 0 }}>
          <div style={eyebrow}>Overall readiness</div>
          <div style={{ fontSize: 17, fontWeight: 800, color: 'var(--fg)', fontFamily: 'var(--font-heading)', letterSpacing: '-0.01em' }}>Ready</div>
        </div>
      </div>

      {/* Completed */}
      <div style={segBorder}>
        <div style={eyebrow}>Completed</div>
        <span style={big}>{completed}</span>
      </div>

      {/* In progress */}
      <div style={segBorder}>
        <div style={eyebrow}>In progress</div>
        <span style={big}>{inProgress}</span>
      </div>

      {/* Total time */}
      <div style={segBorder}>
        <div style={eyebrow}>Total time</div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
          <span style={big}>{estHours}</span>
          <span style={{ fontSize: 15, color: 'var(--muted)', fontWeight: 600 }}>h</span>
        </div>
      </div>
    </div>
  );
}

// Small inline shield icon for the module thumb — avoids bringing in an
// icon lib for this single use. Colour inherits from parent.
function ModuleIcon({ size = 26 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  );
}

type Filter = 'all' | 'in_progress' | 'completed' | 'not_started';

export function MyTraining({ onBack, embedded = false }: { onBack: () => void; embedded?: boolean }) {
  const [assignments, setAssignments] = useState<TrainingAssignment[]>([]);
  const [certificates, setCertificates] = useState<TrainingCertificate[]>([]);
  const [completedCount, setCompletedCount] = useState(0);
  const [nextMilestone, setNextMilestone] = useState(3);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openId, setOpenId] = useState<number | null>(null);
  const [filter, setFilter] = useState<Filter>('all');

  async function reload() {
    setLoading(true);
    try {
      const [a, c] = await Promise.all([
        listMyAssignments(),
        listMyCertificates().catch(() => ({ certificates: [], completed: 0, nextMilestone: 3 } as CertificateListResponse)),
      ]);
      setAssignments(a);
      setCertificates(c.certificates);
      setCompletedCount(c.completed);
      setNextMilestone(c.nextMilestone);
      setError('');
    } catch (err: any) { setError(err.message); }
    finally { setLoading(false); }
  }
  useEffect(() => { reload(); }, []);

  // While a training session is open, render only that — full-screen experience.
  if (openId !== null) {
    const a = assignments.find(x => x.id === openId);
    if (a) {
      return <TrainingSession assignment={a} onExit={async () => { setOpenId(null); await reload(); }} />;
    }
  }

  return (
    <div style={embedded
      ? { padding: 0 }
      : { padding: '40px 28px', maxWidth: 980, margin: '0 auto' }
    }>
      {!embedded && (
        <button onClick={onBack} style={{
          background: 'transparent', border: 'none', color: 'var(--muted)',
          cursor: 'pointer', padding: 0, marginBottom: 16, fontSize: 13,
          display: 'inline-flex', alignItems: 'center', gap: 5,
        }}>← Back to dashboard</button>
      )}

      {/* ── Readiness banner — segmented training header (existing metrics) ── */}
      <TrainingReadinessBanner assignments={assignments} />

      {/* ── Filters ── */}
      {assignments.length > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          margin: '0 0 16px', flexWrap: 'wrap', gap: 10,
        }}>
          <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: 18, margin: 0, color: 'var(--fg)' }}>
            Modules
          </h3>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {([
              ['all',          'All'],
              ['in_progress',  'In progress'],
              ['completed',    'Completed'],
              ['not_started',  'Not started'],
            ] as [Filter, string][]).map(([key, label]) => (
              <button
                key={key}
                className="training-filter-pill"
                data-active={filter === key}
                onClick={() => setFilter(key)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <p style={{ color: 'var(--muted)' }}>Loading…</p>
      ) : error ? (
        <p style={{ color: 'var(--accent-red)' }}>{error}</p>
      ) : assignments.length === 0 ? (
        <div style={{
          border: '2px dashed var(--border-md)', borderRadius: 12,
          padding: '40px 20px', textAlign: 'center', color: 'var(--muted)',
        }}>
          <p style={{ fontSize: 14 }}>You have no training assigned right now.</p>
          <p style={{ fontSize: 12, marginTop: 4 }}>New modules will appear here when your IC assigns them.</p>
        </div>
      ) : (() => {
        // Apply filter, preserve assignment order (backend sorts by assigned_at).
        const filtered = assignments.filter(a => {
          if (filter === 'all')         return true;
          if (filter === 'completed')   return a.status === 'COMPLETED';
          if (filter === 'in_progress') return a.status === 'IN_PROGRESS';
          if (filter === 'not_started') return a.status !== 'COMPLETED' && a.status !== 'IN_PROGRESS';
          return true;
        });
        if (filtered.length === 0) {
          return (
            <div style={{
              border: '1px dashed var(--border-md)', borderRadius: 12,
              padding: 24, textAlign: 'center', color: 'var(--muted)', fontSize: 13,
            }}>
              No modules match this filter.
            </div>
          );
        }
        return (
          <div className="modules-grid" data-tour="training.list">
            {filtered.map((a, i) => (
              <ModuleCard key={a.id} a={a} index={i} onOpen={() => setOpenId(a.id)} />
            ))}
          </div>
        );
      })()}

      {/* ── Collected Badges ── */}
      {(() => {
        const earned = assignments.filter(a => a.status === 'COMPLETED');
        return (
          <div style={{
            marginTop: 32, padding: '20px 22px',
            background: 'var(--surface)', border: '1px solid var(--border-md)',
            borderRadius: 14,
          }}>
            <h3 style={{
              fontFamily: 'var(--font-heading)', fontSize: 16, fontWeight: 700,
              color: 'var(--fg)', marginBottom: 14,
            }}>
              Collected Badges ({earned.length})
            </h3>
            {earned.length === 0 ? (
              <p style={{ fontSize: 13, color: 'var(--muted)', fontStyle: 'italic' }}>
                Complete training to earn your first badge!
              </p>
            ) : (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
                gap: 14,
              }}>
                {earned.map(a => (
                  <div key={a.id} className="badge-card" style={{
                    display: 'flex', alignItems: 'center', gap: 14,
                    padding: '18px 20px',
                    background: 'var(--primary)',
                    color: '#fff', borderRadius: 14,
                    boxShadow: '0 8px 24px rgba(0,103,254,0.28)',
                  }}>
                    <span style={{
                      width: 46, height: 46, borderRadius: 13, flexShrink: 0, display: 'grid', placeItems: 'center',
                      background: 'rgba(255,255,255,0.16)', border: '1px solid rgba(255,255,255,0.24)',
                    }}>
                      <svg width="23" height="23" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><path d="m9 12 2 2 4-4" /></svg>
                    </span>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{
                        fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
                        opacity: 0.72, letterSpacing: 1.2, marginBottom: 3,
                      }}>Certified</div>
                      <div style={{
                        fontSize: 14, fontWeight: 700,
                        fontFamily: 'var(--font-heading)',
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                        marginBottom: 3,
                      }}>{a.moduleTitle}</div>
                      <div style={{ fontSize: 11, opacity: 0.75 }}>
                        Score {a.bestQuizScore}% · {a.completedAt
                          ? new Date(a.completedAt).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
                          : 'Completed'}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })()}

      {/* ── Certificates (3 / 5 / 10 / 15 + modules completed) ── */}
      <CertificatesSection
        certificates={certificates}
        completedCount={completedCount}
        nextMilestone={nextMilestone}
      />
    </div>
  );
}

// ── Certificates section ───────────────────────────────────────────────────

function CertificatesSection({
  certificates,
  completedCount,
  nextMilestone,
}: {
  certificates: TrainingCertificate[];
  completedCount: number;
  nextMilestone: number;
}) {
  const progress = nextMilestone > 0 ? Math.min(100, Math.round((completedCount / nextMilestone) * 100)) : 0;

  return (
    <div style={{
      marginTop: 24,
      padding: '20px 22px',
      background: 'var(--surface)',
      border: '1px solid var(--border-md)',
      borderRadius: 14,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 14, gap: 12, flexWrap: 'wrap' }}>
        <h3 style={{
          fontFamily: 'var(--font-heading)', fontSize: 16, fontWeight: 700,
          color: 'var(--fg)', margin: 0,
        }}>
          Certificates ({certificates.length})
        </h3>
        <div style={{ fontSize: 11, color: 'var(--muted)' }}>
          {completedCount} module{completedCount === 1 ? '' : 's'} completed · next cert at {nextMilestone}
        </div>
      </div>

      {/* Progress bar toward next milestone */}
      <div
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={nextMilestone}
        aria-valuenow={completedCount}
        style={{
          height: 8, background: 'var(--faint)', borderRadius: 999,
          overflow: 'hidden', marginBottom: 18,
        }}
      >
        <div style={{
          width: `${progress}%`, height: '100%',
          background: 'var(--cs-amber)',
          borderRadius: 999,
          transition: 'width 400ms ease-out',
        }} />
      </div>

      {certificates.length === 0 ? (
        <p style={{ fontSize: 13, color: 'var(--muted)', fontStyle: 'italic', margin: 0 }}>
          Complete {Math.max(1, nextMilestone - completedCount)} more module{nextMilestone - completedCount === 1 ? '' : 's'} to unlock your first certificate.
        </p>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
          gap: 14,
        }}>
          {certificates.map(c => (
            <CertificateCard key={c.id} cert={c} />
          ))}
        </div>
      )}
    </div>
  );
}

function CertificateCard({ cert }: { cert: TrainingCertificate }) {
  const [opening, setOpening] = useState(false);
  const [err, setErr] = useState('');

  async function handleOpen() {
    setOpening(true); setErr('');
    try {
      await openCertificate(cert.id);
    } catch (e: any) {
      setErr(e.message || 'Could not open certificate');
    } finally {
      setOpening(false);
    }
  }

  const issued = new Date(cert.issuedAt);
  return (
    <button
      onClick={handleOpen}
      disabled={opening}
      style={{
        cursor: opening ? 'wait' : 'pointer',
        textAlign: 'left',
        fontFamily: 'inherit',
        padding: '22px 22px',
        borderRadius: 14,
        border: 'none',
        background: 'var(--primary)',
        color: '#fff',
        boxShadow: '0 10px 24px rgba(0,103,254,0.28)',
        display: 'flex', alignItems: 'center', gap: 16,
        position: 'relative', overflow: 'hidden',
        transition: 'transform 180ms ease-out, box-shadow 180ms ease-out',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 16px 32px rgba(0,103,254,0.38)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 10px 24px rgba(0,103,254,0.28)';
      }}
    >
      <div style={{
        width: 56, height: 56, borderRadius: '50%',
        background: 'var(--cs-amber)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0, color: '#3d1c00',
        boxShadow: 'inset 0 0 0 3px rgba(255,255,255,0.3)',
      }}>
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="6" /><path d="M15.5 13.5 17 22l-5-3-5 3 1.5-8.5" /></svg>
      </div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 10, fontWeight: 700, opacity: 0.75, letterSpacing: 1.4, textTransform: 'uppercase', marginBottom: 4 }}>
          Certificate #{cert.milestone}
        </div>
        <div style={{ fontFamily: 'var(--font-heading)', fontSize: 15, fontWeight: 700, marginBottom: 4 }}>
          {cert.milestone} Modules Completed
        </div>
        <div style={{ fontSize: 11, opacity: 0.78 }}>
          Issued {issued.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
        </div>
        {err && <div style={{ fontSize: 11, color: '#fecaca', marginTop: 4 }}>{err}</div>}
      </div>
      <span style={{
        background: 'rgba(255,255,255,0.16)', backdropFilter: 'blur(6px)',
        padding: '7px 12px', borderRadius: 8, fontSize: 11, fontWeight: 700,
        flexShrink: 0, letterSpacing: 0.5,
      }}>
        {opening ? 'Opening…' : 'View & print'}
      </span>
    </button>
  );
}

// Module tile — visual adapt of the Crisis inspiration (aspect-ratio thumb,
// icon overlay, duration chip, progress bar). All fields + CTA behaviour
// match the original AssignmentCard so nothing backend-wise changes.
// Staggered entry via animation-delay tied to grid index.
function ModuleCard({ a, index, onOpen }: { a: TrainingAssignment; index: number; onOpen: () => void }) {
  const completed = a.status === 'COMPLETED';
  const inProgress = a.status === 'IN_PROGRESS';
  const overdue = new Date(a.deadlineAt) < new Date() && !completed;
  const progress = completed ? 100 : (a.videoProgressPercent || 0);
  const cta = completed ? 'Review' : inProgress || progress > 0 ? 'Continue' : 'Start';

  // Duration text: rough heuristic — we don't have an explicit minutes field
  // on TrainingAssignment, so fall back to "Module" framing using deadlineAt.
  const deadline = new Date(a.deadlineAt).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });

  const metaLine = completed
    ? `Completed · Score ${a.bestQuizScore ?? '—'}%`
    : overdue
      ? `Overdue — was due ${deadline}`
      : `${progress}% complete · Due ${deadline}`;

  // If the admin uploaded a cover image for this module, use it as the
  // card thumb (with a dark-to-transparent overlay so the status chip in
  // the corner stays readable). Otherwise keep the default gradient +
  // shield-icon thumb.
  const thumbUrl = a.thumbnailUrl || null;

  return (
    <button
      className="module-card"
      onClick={onOpen}
      style={{ animationDelay: `${index * 60}ms` }}
    >
      <div
        className="module-thumb"
        style={thumbUrl
          ? {
              backgroundImage: `url("${thumbUrl}")`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }
          : undefined}
      >
        {!thumbUrl && <div className="module-thumb-icon"><ModuleIcon size={28} /></div>}
        <div className="module-duration">
          {completed ? 'Completed' : overdue ? 'Overdue' : inProgress ? 'In progress' : 'Not started'}
        </div>
      </div>
      <div className="module-body">
        <div className="module-title">{a.moduleTitle}</div>
        <div className="module-desc">
          {a.moduleDescription || 'Crisis management module assigned by your Incident Commander.'}
        </div>
        <div className="module-progress">
          <div className="module-progress-fill" style={{ width: `${progress}%` }} />
        </div>
        <div className="module-meta">
          <span style={{ color: overdue ? 'var(--accent-red)' : 'var(--subtle)' }}>{metaLine}</span>
          <span style={{
            padding: '3px 10px', borderRadius: 6,
            fontSize: 11, fontWeight: 700,
            background: completed ? 'var(--faint)' : 'var(--primary)',
            color: completed ? 'var(--muted)' : 'var(--on-primary)',
          }}>
            {cta}
          </span>
        </div>
      </div>
    </button>
  );
}
